import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Bell, Scale, ShieldCheck, FileText, LogOut, Trash2, ChevronRight, CreditCard, Stethoscope, CheckCircle2, Mail, Camera, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit";
import { formatDate } from "@/lib/files";
import ReferralCard from "@/components/ReferralCard";
import ContactModal from "@/components/ContactModal";
import InstallPWA from "@/components/InstallPWA";
import EditProfileModal from "@/components/EditProfileModal";
import VerifyEmailModal from "@/components/VerifyEmailModal";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  useEffect(() => {
    apiFetch("/billing/status").then(setStatus).catch(() => {});
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await apiFetch("/billing/portal", { method: "POST" });
      window.location.href = res.portal_url;
    } catch (err) {
      toast.error(err.message || "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and all data? This cannot be undone.")) return;
    try {
      await apiFetch("/auth/me", { method: "DELETE" });
      toast.success("Account deleted");
      logout();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const sub = status?.subscription;
  const isActive = sub?.is_active;
  const isVet = user?.role === "vet";

  const menu = [
    { icon: Bell, label: "Notification settings", to: "/app/notification-settings" },
    !isVet && { icon: Bell, label: "Vaccine & visit reminders", to: "/app/reminders" },
    isVet && { icon: Stethoscope, label: "Vet dashboard", to: "/app/vet-dashboard" },
    { icon: FileText, label: "Terms of Service", to: "/legal/terms" },
    { icon: ShieldCheck, label: "Privacy Policy", to: "/legal/privacy" },
    { icon: Scale, label: "Medical Disclaimer", to: "/legal/disclaimer" },
  ].filter(Boolean);

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="Profile" />

      {/* Identity */}
      <div className="flex items-center gap-4 p-5 rounded-3xl bg-white border border-[#E8E3DA] mb-5">
        <div className="h-16 w-16 rounded-full bg-[#E86A45] text-white flex items-center justify-center font-display text-2xl font-black">
          {(user?.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl font-extrabold truncate">{user?.name}</p>
          <p className="text-[#4A4746] truncate">{user?.email}</p>
          <span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#F2EFE9] text-[#4A4746] capitalize">
            {isVet ? "Veterinary clinic" : "Pet parent"}
          </span>
        </div>
        <button
          data-testid="edit-profile-btn"
          onClick={() => setShowEdit(true)}
          className="shrink-0 flex items-center gap-1.5 rounded-full border border-[#E8E3DA] text-[#4A4746] font-bold text-sm px-3.5 py-2 hover:bg-[#F2EFE9] transition-colors"
        >
          <Pencil size={15} /> Edit
        </button>
      </div>

      {/* Subscription */}
      <div className={`p-5 rounded-3xl mb-5 ${isActive ? "bg-[#E8F0E5]" : "bg-[#F6DED5]"}`} data-testid="profile-subscription-card">
        <div className="flex items-center gap-2 mb-1">
          {isActive ? <CheckCircle2 size={20} color="#6F8F72" /> : <Sparkles size={20} color="#E86A45" />}
          <p className="font-display text-lg font-extrabold">
            {isActive ? "Subscription active" : isVet ? "Free listing" : "Free plan"}
          </p>
        </div>
        {isActive && sub?.expires_at ? (
          <p className="text-sm text-[#4A4746]">
            {sub.cancel_at_period_end ? "Ends" : "Renews"} {formatDate(sub.expires_at)}
          </p>
        ) : !isVet ? (
          <p className="text-sm text-[#4A4746]">
            {status?.ai_usage_this_month || 0} of {status?.free_tier_limit || 3} free AI questions used this month
          </p>
        ) : (
          <p className="text-sm text-[#4A4746]">Upgrade to feature your clinic at the top of search.</p>
        )}

        <div className="flex gap-3 mt-4">
          <button
            data-testid="profile-manage-plan-btn"
            onClick={() => navigate("/app/subscription")}
            className="flex-1 rounded-full bg-[#E86A45] text-white font-extrabold py-2.5 hover:bg-[#d65f3c] transition-colors"
          >
            {isActive ? "Change plan" : "Upgrade"}
          </button>
          {status?.subscription?.plan && (
            <button
              data-testid="profile-billing-portal-btn"
              onClick={openPortal}
              disabled={portalLoading}
              className="flex items-center justify-center gap-2 rounded-full border-2 border-[#E86A45]/40 text-[#B54828] font-extrabold px-5 py-2.5 disabled:opacity-60"
            >
              <CreditCard size={17} /> {portalLoading ? "Opening…" : "Billing"}
            </button>
          )}
        </div>
      </div>

      {/* Referral */}
      {!isVet && <ReferralCard />}

      {/* Contact us */}
      <div className="p-5 rounded-3xl bg-white border border-[#E8E3DA] mb-5" data-testid="profile-contact-card">
        <div className="flex items-center gap-2 mb-1">
          <Camera size={20} color="#6F8F72" />
          <p className="font-display text-lg font-extrabold">Get in touch</p>
        </div>
        <p className="text-sm text-[#4A4746] leading-relaxed">
          Have a suggestion, comment, or need support? We&apos;d love to hear from you. Want your pet
          featured in our gallery? — send us a note and a photo of your pup for{" "}
          <a href="https://happybigdogs.net" target="_blank" rel="noopener noreferrer" className="font-bold text-[#E86A45]">
            happybigdogs.net
          </a>
          .
        </p>
        <button
          onClick={() => setShowContact(true)}
          data-testid="profile-contact-btn"
          className="inline-flex items-center gap-2 mt-4 rounded-full bg-[#6F8F72] text-white font-extrabold px-5 py-2.5 hover:bg-[#61805f] transition-colors"
        >
          <Mail size={17} /> Contact us
        </button>
      </div>

      <ContactModal open={showContact} onClose={() => setShowContact(false)} defaultName={user?.name} />
      <EditProfileModal open={showEdit} onClose={() => setShowEdit(false)} onEmailChanged={() => setShowVerify(true)} />
      <VerifyEmailModal open={showVerify} onClose={() => setShowVerify(false)} />

      {/* Install as app (PWA) — hidden automatically once installed */}
      <div className="p-5 rounded-3xl bg-white border border-[#E8E3DA] mb-5" data-testid="profile-install-card">
        <div className="flex items-center gap-2 mb-1">
          <Download size={20} color="#6F8F72" />
          <p className="font-display text-lg font-extrabold">Install Wooflect</p>
        </div>
        <p className="text-sm text-[#4A4746] leading-relaxed">
          Add Wooflect to your phone&apos;s home screen to use it like a native app on
          Android &amp; iPhone — fullscreen, fast, no app store required.
        </p>
        <div className="mt-4">
          <InstallPWA />
        </div>
      </div>

      {/* Menu */}
      <div className="rounded-3xl bg-white border border-[#E8E3DA] overflow-hidden mb-5">
        {menu.map((m, i) => (
          <button
            key={m.label}
            data-testid={`profile-menu-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => navigate(m.to)}
            className={`w-full flex items-center gap-3 px-5 py-4 hover:bg-[#F2EFE9] transition-colors text-left ${i !== 0 ? "border-t border-[#E8E3DA]" : ""}`}
          >
            <m.icon size={20} color="#4A4746" />
            <span className="flex-1 font-bold text-[#2D2B2A]">{m.label}</span>
            <ChevronRight size={18} color="#9A938B" />
          </button>
        ))}
      </div>

      {/* Danger / logout */}
      <button
        data-testid="profile-logout-btn"
        onClick={() => { logout(); navigate("/", { replace: true }); }}
        className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-[#E8E3DA] font-extrabold py-3 mb-3 hover:border-[#C4BFB8] transition-colors"
      >
        <LogOut size={18} /> Log out
      </button>
      <button
        data-testid="profile-delete-account-btn"
        onClick={deleteAccount}
        className="w-full flex items-center justify-center gap-2 text-[#D9534F] font-bold py-2"
      >
        <Trash2 size={17} /> Delete account
      </button>
    </div>
  );
}
